/*
 * Global formatting library
 */
import axios from 'axios'
import moment from 'moment'
import { Config } from '@ciitizen/api'

const SUPPORTED_MIMETYPES = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  dicom: 'application/dicom',
  dcm: 'application/dicom'
}
let microName = [],
  count = 0

export default {
  /**
   * @name ciiFormatDate
   * @function
   * @param {String} date - date string
   * @param {String} format - format string
   * @returns {String or undefined}
   */
  ciiFormatDate(dt, options = { year: 'numeric', month: 'short', day: 'numeric' }, toUTC = false) {
    if (!dt) {
      return null
    }
    let date = new Date(dt)
    let dateOptions = options
    if (toUTC) {
      dateOptions.timeZone = 'UTC'
      return date.toLocaleDateString('en-US', dateOptions)
    } else {
      return date.toLocaleDateString('en-US', dateOptions)
    }
  },
  momentDateFormat(date, format) {
    return moment(date).format(format)
  },

  /**
   * @name getImageBlob
   * @function
   * @param {String} imgHREF - image href
   * @param {String} http - http object to fetch binary image
   * @returns {String or undefined}
   */
  getImageBlob(imgHREF, token = null) {
    return new Promise((resolve, reject) => {
      const config = {
        responseType: 'blob'
      }
      const sharingToken = token || localStorage.getItem('default_auth_token')
      if (sharingToken) {
        config.headers = {}
        config.headers['Authorization'] = `Bearer ${sharingToken}`
      }
      axios
        .get(imgHREF, config)
        .then(({ data }) => {
          resolve(window.URL.createObjectURL(data))
        })
        .catch(e => {
          reject(e)
        })
    })
  },

  /**
   * Get pages for documents for pdf viewer
   * @param store
   * @param payload
   * @returns {Promise<AxiosResponse<any> | never>}
   */
  async fetchRecordPages(payload) {
    try {
      const sharingToken = payload.token || localStorage.getItem('default_auth_token')
      const headers = {
        Authorization: `Bearer ${sharingToken}`,
        'Content-Type': 'application/json'
      }
      const { data } = await axios.get(`${Config}/objectstore/pages`, {
        headers: headers,
        params: {
          q: payload.q,
          orderBy: payload.orderBy,
          asc: payload.asc,
          limit: payload.limit,
          offset: payload.offset,
          grantor: payload.grantor,
          parentResourceType: 'Document',
          parentId: payload.parentId
        }
      })
      return data
    } catch (err) {
      console.log(err)
      throw err
    }
  },
  /**
   * @name downloadDocument
   * @function
   * @param {String} downloadUrl - url of document string
   * @param {Object} http - http object
   * @param {String} filename - name of file string
   * @returns {Promise} - downloads document
   */
  downloadDocument(downloadUrl, filename, sharedToken = null) {
    let ext = filename.split('.')
    ext = ext[ext.length - 1]
    const mimetype = SUPPORTED_MIMETYPES[ext.toLowerCase()]
    let config = { responseType: 'blob' }
    const authtoken = localStorage.getItem('default_auth_token')
    const token = authtoken ? authtoken : sharedToken
    if (token) {
      config.headers = {}
      config.headers['Authorization'] = `Bearer ${token}`
    }
    axios.get(downloadUrl, config).then(({ data }) => {
      let blob = new Blob([data], { type: mimetype })
      let url
      if (window.navigator.msSaveOrOpenBlob) {
        // IE 11+
        window.navigator.msSaveOrOpenBlob(blob, filename)
      } else if (window.navigator.userAgent.match('FxiOS')) {
        // FF iOS
        alert('Cannot display on FF iOS')
      } else if (window.navigator.userAgent.match('CriOS')) {
        // Chrome iOS
        var reader = new FileReader()
        reader.onloadend = function () {
          window.open(reader.result)
        }
        reader.readAsDataURL(blob)
      } else if (
        window.navigator.userAgent.match(/iPad/i) ||
        window.navigator.userAgent.match(/iPhone/i)
      ) {
        // Safari & Opera iOS
        url = window.URL.createObjectURL(blob)
        window.location.href = url
      } else {
        const a = document.createElement('a')
        url = window.URL.createObjectURL(blob)
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        setTimeout(() => {
          window.URL.revokeObjectURL(url)
          a.remove()
        }, 10)
      }
    })
  },

  /*
   * @Work check if mobile UI or desktop
   * @name isDesktop
   * @function
   * @returns {Bool or undefined}
   */
  checkIfDesktop() {
    return window.innerWidth > 561
  },

  /*
   * @name mount
   * @function
   * @param {String} domId - Id registered for el dom
   * @returns {String or undefined}
   */
  createComponentRootElement(domId) {
    const spaWrapper = document.getElementById('app-body') || document.body
    const newComponent = Object.assign(document.createElement('div'), {
      id: domId
    })
    spaWrapper.appendChild(newComponent)
  },

  /**
   * @name clearComponentRootElement
   * @function
   * @returns {}
   */
  clearComponentRootElement() {
    const spaWrapper = document.getElementById('app-body') || document.body
    while (spaWrapper.firstChild) {
      spaWrapper.removeChild(spaWrapper.firstChild)
    }
  },

  /**
   * Removes all routes except user id and appends new path to navigate user
   * @name manageProfileRouting
   * @function
   * @param {String} path - name of path user wants to append to current path
   * @returns {}
   */
  manageProfileRouting({ path, query, route, router }) {
    let pathParams = route.path.split('/')
    pathParams.length = 2
    let newPath = [...pathParams, path]
    let newPathStr = newPath.join('/')
    router.push({ path: newPathStr, query: query })
  },
  /**
   * Removes searchView query param from route
   * @name clearSearchMode
   * @function
   * @param {Object} router - router for the microfrontend thats being re-routed
   * @returns {}
   */
  clearSearchMode({ router }) {
    router.push({
      query: {
        searchView: undefined
      }
    })
  },
  /**
   * Capitalize string
   * @name capitalized
   * @function
   * @param {text} - Text to be capitalized
   * @returns Capitalized string
   */
  capitalizedText(text) {
    return text
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.toLowerCase().slice(1))
      .join(' ')
  },
  async promiseAllApis(totalApi, micro) {
    microName.push(micro)
    //wait until all api's fetch data
    await this.waitUntil(() => totalApi === microName.length)
    await Promise.all(microName)
    count++
    if (count === totalApi) {
      // clear data on last iteration
      this.clearPromises()
    }
  },
  clearPromises() {
    microName = []
    count = 0
  },
  waitUntil(condition) {
    return new Promise(resolve => {
      let interval = setInterval(() => {
        if (!condition()) {
          return
        }
        clearInterval(interval)
        resolve()
      }, 10)
    })
  },
  callSegmentTrack({ name, ...fields }) {
    const segment = window.analytics
    segment.track(name, fields)
  },
  callSegmentIdentify(userId, { ...traits }) {
    const segment = window.analytics
    segment.identify(userId, traits)
  },
  showNavigationOptions() {
    return window.innerWidth > 1070
  },
  isIphone() {
    if (typeof window === `undefined` || typeof navigator === `undefined`) return false

    return /iPhone/i.test(
      navigator.userAgent ||
        navigator.vendor ||
        (window.opera && window.opera.toString() === `[object Opera]`)
    )
  },
  isIpad() {
    if (typeof window === `undefined` || typeof navigator === `undefined`) return false

    return /iPad/i.test(
      navigator.userAgent ||
        navigator.vendor ||
        (window.opera && window.opera.toString() === `[object Opera]`)
    )
  },
  formatCreatedDate(date) {
    // need to re-format the date to match some expected format on the BE
    let localCreatedDate = new Date(date)
    let day =
      localCreatedDate.getDate() < 10
        ? `0${localCreatedDate.getDate()}`
        : localCreatedDate.getDate()
    let month =
      localCreatedDate.getMonth() + 1 < 10
        ? `0${localCreatedDate.getMonth() + 1}`
        : localCreatedDate.getMonth() + 1
    return `${localCreatedDate.getFullYear()}-${month}-${day}`
  },
  calculateBoundingBox({
    originalWidth,
    originalHeight,
    currentWidth,
    currentHeight,
    boundingWidth,
    boundingHeight,
    boundingOriginX,
    boundingOriginY,
    offsetLeft
  }) {
    let widthRatio = currentWidth / originalWidth
    let heightRatio = currentHeight / originalHeight
    let calculatedBoundingWidth = boundingWidth * widthRatio
    let calculatedBoundingHeight = boundingHeight * heightRatio
    let originX = boundingOriginX * widthRatio
    let originY = boundingOriginY * heightRatio

    let output = {
      width: calculatedBoundingWidth + 5, // adding a small padding
      height: calculatedBoundingHeight + 2, // adding a small padding
      originX: originX - 5, // removing it upon positioning the element
      originY: originY + 128, // removing it upon positioning the element
      offsetLeft: offsetLeft
    }

    return output
  },
  SUPPORTED_MIMETYPES: SUPPORTED_MIMETYPES
}
